/**
 * Login Page Component
 * Two-column layout with marketing panel and login form
 * Matches GraymatterLab design system
 */

import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@contexts/AuthContext";
import { useTenant } from "@contexts/TenantContext";
import { isStudioSso, studioLoginUrl } from "@/config/studio";
import agentHubLogo from "../../assets/graymatterlab_agent_hub.png";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/**
 * studio_sso: Hub has no login form. Bounce to Studio's login, preserving the
 * current URL as the return target so the user lands back where they started.
 *
 * Guarded: only bounce when we're genuinely logged out. Studio returns the user
 * to this same /login URL after auth; without the guard the redirect re-fired
 * unconditionally and looped forever even with a valid session (task_077). Wait
 * for auth to resolve, send authenticated users into the app, and fire the
 * redirect at most once.
 */
function StudioRedirect() {
  const { user, isLoading } = useAuth();
  const redirectStarted = useRef(false);

  useEffect(() => {
    if (!isLoading && !user && !redirectStarted.current) {
      redirectStarted.current = true;
      window.location.assign(studioLoginUrl());
    }
  }, [isLoading, user]);

  // Returned from Studio with a valid gml_sso session → go to the app instead
  // of bouncing back to Studio.
  if (!isLoading && user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-800">
      <div className="flex flex-col items-center gap-4">
        <img
          src={agentHubLogo}
          alt="GraymatterLab Agent Hub"
          className="h-12 w-auto dark:invert"
        />
        <svg
          className="animate-spin h-6 w-6 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p className="text-gray-500 dark:text-gray-400">Redirecting to Studio…</p>
      </div>
    </div>
  );
}

export function LoginPage() {
  // studio_sso: redirect to Studio. hub_jwt: render the local login form.
  if (isStudioSso()) {
    return <StudioRedirect />;
  }
  return <HubJwtLoginPage />;
}

function HubJwtLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuth();
  const {
    tenantError,
    isLoading: tenantLoading,
    resetToDefaultTenant,
  } = useTenant();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get redirect path from location state or default to dashboard
  const from = (location.state as { from?: string })?.from || "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      const error = err as { detail?: string; message?: string };
      setError(
        error.detail ||
          error.message ||
          "Login failed. Please check your credentials.",
      );
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Marketing/Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gray-50 dark:bg-gray-900 items-center justify-end pr-16 xl:pr-24">
        <div className="max-w-md">
          {/* Logo */}
          <img
            src={agentHubLogo}
            alt="GraymatterLab Agent Hub"
            className="h-28 w-auto mb-8 dark:invert -ml-4"
          />

          {/* Greeting */}
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">
            {getGreeting()}
          </p>

          {/* Hero Headline */}
          <h1 className="text-3xl xl:text-4xl font-bold text-gray-900 dark:text-white leading-tight mb-6">
            Learn to Build{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">
              AI Agents
            </span>
          </h1>

          {/* Value Proposition */}
          <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
            Your hub for learning how to design, build, and deploy intelligent
            AI agents. Explore workshops, guides, and resources to master agent
            development.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-start pl-16 xl:pl-24 px-6 py-12 bg-white dark:bg-gray-800">
        <div className="w-full max-w-sm">
          {/* Mobile Logo - Only shown on smaller screens */}
          <div className="lg:hidden mb-8">
            <img
              src={agentHubLogo}
              alt="GraymatterLab Agent Hub"
              className="h-12 mx-auto dark:invert"
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Welcome back
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Sign in to continue building
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Tenant Error Message */}
            {tenantError && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 px-4 py-3 rounded-lg text-sm">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <p>{tenantError}</p>
                    <button
                      type="button"
                      onClick={resetToDefaultTenant}
                      className="mt-2 text-sm font-medium text-amber-800 dark:text-amber-300 hover:underline"
                    >
                      Reset now
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Login Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Email Field */}
            <div>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors"
                placeholder="Email address"
              />
            </div>

            {/* Password Field */}
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors pr-12"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || tenantLoading || !!tenantError}
              className="w-full py-3 px-4 bg-gradient-to-r from-gray-800 to-gray-900
                       dark:from-gray-700 dark:to-gray-800
                       text-white font-medium rounded-lg
                       hover:from-gray-700 hover:to-gray-800
                       dark:hover:from-gray-600 dark:hover:to-gray-700
                       focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Register Link */}
          <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
            New to GraymatterLab?{" "}
            <Link
              to="/register"
              className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            >
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
